import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Link,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { Client } from '@microsoft/microsoft-graph-client';
import { ServicePrincipal } from '@microsoft/microsoft-graph-types';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import BusinessIcon from '@mui/icons-material/Business'; // For Service Principal
import VpnKeyIcon from '@mui/icons-material/VpnKey'; // For Reply URLs
import NotesIcon from '@mui/icons-material/Notes';
import TagIcon from '@mui/icons-material/Tag';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import WebIcon from '@mui/icons-material/Web';
import LogoutIcon from '@mui/icons-material/Logout';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For the "About" section
import FingerprintIcon from '@mui/icons-material/Fingerprint'; // For App ID
import StorageIcon from '@mui/icons-material/Storage'; // For Tenant
import BuildIcon from '@mui/icons-material/Build'; // For Configuration
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'; // For Runtime
import SecurityUpdateGoodIcon from '@mui/icons-material/SecurityUpdateGood'; // For Security Context
import Button from '@mui/material/Button'; // Import Button for "Show More/Less"
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface ServicePrincipalOverviewProps {
  servicePrincipalId: string | null;
  graphAccessToken: string;
  appDisplayName?: string; // To compare with SP display name
}

const ServicePrincipalOverview: React.FC<ServicePrincipalOverviewProps> = ({
  servicePrincipalId,
  graphAccessToken,
  appDisplayName,
}) => {
  const [spDetails, setSpDetails] = useState<ServicePrincipal | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllDetails, setShowAllDetails] = useState<boolean>(false);

  useEffect(() => {
    if (!servicePrincipalId || !graphAccessToken) {
      if (!servicePrincipalId) {
        setError("Service Principal ID is not available. This might happen if the application hasn't been provisioned in the tenant yet.");
      }
      return;
    }

    const fetchServicePrincipalDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const client = Client.init({
          authProvider: (done) => done(null, graphAccessToken),
        });
        const response: ServicePrincipal = await client
          .api(`/servicePrincipals/${servicePrincipalId}`)
          .select('id,appId,displayName,servicePrincipalType,accountEnabled,appRoleAssignmentRequired,replyUrls,logoutUrl,tags,notes,signInAudience,loginUrl,homepage') // Added signInAudience, loginUrl, homepage
          .get();
        setSpDetails(response);
      } catch (err: any) {
        console.error('Error fetching service principal details:', err);
        setError(`Failed to fetch service principal details: ${err.message || 'Unknown error'}`);
        setSpDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchServicePrincipalDetails();
  }, [servicePrincipalId, graphAccessToken]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Service Principal details...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!spDetails) {
    return <Alert severity="info" sx={{ m: 2 }}>No Service Principal details to display.</Alert>;
  }

  const renderDetailItem = (label: string, value: React.ReactNode, icon?: React.ReactNode) => (
    <ListItem sx={{ py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)', '&:last-child': { borderBottom: 'none'} }}>
      {icon && <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>{icon}</ListItemIcon>}
      <ListItemText
        primary={label}
        secondary={value || <Typography component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Not set</Typography>}
        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
        secondaryTypographyProps={{
          fontSize: '0.85rem',
          color: 'text.primary',
          whiteSpace: 'pre-wrap',
          sx: { wordBreak: 'break-word' }
        }}
      />
    </ListItem>
  );

  const detailItems = spDetails ? [
    { label: "Display Name", value: spDetails.displayName, icon: <InfoIcon fontSize="small" /> },
    { label: "Service Principal ID (Object ID)", value: spDetails.id, icon: <FingerprintIcon fontSize="small" /> },
    { label: "Application (Client) ID", value: spDetails.appId, icon: <FingerprintIcon fontSize="small" /> },
    { label: "Service Principal Type", value: spDetails.servicePrincipalType, icon: <InfoIcon fontSize="small" /> },
    { label: "Sign-in Audience", value: spDetails.signInAudience, icon: <WebIcon fontSize="small" /> },
    {
      label: "Account Enabled (for sign-in)",
      value: spDetails.accountEnabled ? (
        <Chip icon={<CheckCircleOutlineIcon />} label="Enabled" color="success" size="small" variant="outlined" />
      ) : (
        <Chip icon={<HighlightOffIcon />} label="Disabled" color="error" size="small" variant="outlined" />
      ),
      icon: spDetails.accountEnabled ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />
    },
    {
      label: "Assignment Required (for access)",
      value: spDetails.appRoleAssignmentRequired ? (
        <Chip icon={<CheckCircleOutlineIcon />} label="Yes" color="warning" size="small" variant="outlined" />
      ) : (
        <Chip icon={<HighlightOffIcon />} label="No" color="default" size="small" variant="outlined" />
      ),
      icon: <InfoIcon fontSize="small" />
    },
    { label: "Homepage URL", value: spDetails.homepage ? <Link href={spDetails.homepage} target="_blank" rel="noopener noreferrer">{spDetails.homepage}</Link> : null, icon: <WebIcon fontSize="small" /> },
    { label: "Login URL (alternative to homepage)", value: spDetails.loginUrl ? <Link href={spDetails.loginUrl} target="_blank" rel="noopener noreferrer">{spDetails.loginUrl}</Link> : null, icon: <WebIcon fontSize="small" /> },
    { label: "Logout URL", value: spDetails.logoutUrl ? <Link href={spDetails.logoutUrl} target="_blank" rel="noopener noreferrer">{spDetails.logoutUrl}</Link> : null, icon: <LogoutIcon fontSize="small" /> },
    {
      label: "Reply URLs (Redirect URIs)",
      value: spDetails.replyUrls && spDetails.replyUrls.length > 0 ? (
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {spDetails.replyUrls.map((url, index) => (
            <ListItem key={index} disablePadding sx={{ pl: 2 }}>
              <ListItemText primary={url} primaryTypographyProps={{ fontSize: '0.8rem' }} />
            </ListItem>
          ))}
        </List>
      ) : "No reply URLs configured on Service Principal.",
      icon: <VpnKeyIcon fontSize="small" />
    },
    {
      label: "Tags",
      value: spDetails.tags && spDetails.tags.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {spDetails.tags.map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      ) : "No tags.",
      icon: <TagIcon fontSize="small" />
    },
    { label: "Notes", value: spDetails.notes, icon: <NotesIcon fontSize="small" /> }
  ] : [];

  const initiallyVisibleItems = detailItems.slice(0, 7);
  const hiddenItems = detailItems.slice(7);

  return (
    <Card variant="outlined">
        <CardContent
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
            borderBottom: '1px solid #c5cae9',
            mb: 0,
            pb: 2,
            pt: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                backgroundColor: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}
            >
              <BusinessIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.3rem",
                fontWeight: 600,
                color: "#283593",
                letterSpacing: "0.01em",
                flexGrow: 1
              }}
            >
              Service Principal Overview
            </Typography>
            <Chip
              size="small"
              label="Enterprise App"
              sx={{
                backgroundColor: '#c5cae9',
                color: '#283593',
                fontWeight: 500
              }}
            />
          </Box>
        </CardContent>

      <CardContent>
        {appDisplayName && spDetails.displayName !== appDisplayName && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The Service Principal display name ("{spDetails.displayName}") differs from the Application Registration display name ("{appDisplayName}").
          </Alert>
        )}

        {spDetails && (
          <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2, mb: 3 }}>
            <List dense disablePadding>
              {initiallyVisibleItems.map(item => renderDetailItem(item.label, item.value, item.icon))}
              {showAllDetails && hiddenItems.map(item => renderDetailItem(item.label, item.value, item.icon))}
            </List>
            {hiddenItems.length > 0 && (
              <Box sx={{ textAlign: 'center', py: 1, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <Button
                  onClick={() => setShowAllDetails(!showAllDetails)}
                  size="small"
                  endIcon={showAllDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                >
                  {showAllDetails ? 'Show Less' : `Show More (${hiddenItems.length} items)`}
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* ABOUT SERVICE PRINCIPAL Section */}
        <Accordion sx={{ boxShadow: 'none', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="about-sp-content"
            id="about-sp-header"
            sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
          >
            <HelpOutlineIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>About Service Principals</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, backgroundColor: '#fcfcfc' }}>
            <Typography variant="body2" paragraph>
              In Microsoft Entra ID, an <strong>Application Registration</strong> (or App Object) acts as the global blueprint or template for your application. It defines what your application can do, who can use it, and what APIs it can access.
            </Typography>
            <Typography variant="body2" paragraph>
              A <strong>Service Principal</strong> (or Enterprise Application) is the local instance or representation of that application within a specific tenant. When an application is given permission to access resources in a tenant (e.g., through user consent or admin consent), a service principal object is created in that tenant.
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <BuildIcon sx={{ mr: 1, color: 'primary.dark' }} /> Application Registration (Blueprint)
                  </Typography>
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemText primary="Global definition of the application." secondary="Exists once, in the app's home tenant." />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Defines properties like name, logo, supported account types, API permissions requested, app roles defined, redirect URIs." />
                    </ListItem>
                     <ListItem>
                      <ListItemText primary="Managed in 'App Registrations' in the Azure portal." />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <PlayCircleOutlineIcon sx={{ mr: 1, color: 'success.dark' }} /> Service Principal (Local Instance)
                  </Typography>
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemText primary="Local representation in a specific tenant." secondary="Created when the app is provisioned/consented in a tenant." />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Governs what the app can actually do in *this* tenant." secondary="Holds granted permissions, user assignments, Conditional Access policy assignments."/>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Can have its own properties like 'enabled for sign-in', 'assignment required', specific reply URLs (for gallery apps), and notes/tags." />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Managed in 'Enterprise Applications' in the Azure portal." />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              <strong>Key Differences & Relationship:</strong>
            </Typography>
            <List dense disablePadding sx={{ '& .MuiListItem-root': { alignItems: 'flex-start', py: 0.5 } }}>
              <ListItem>
                <ListItemIcon sx={{minWidth: 30, mt: 0.5}}><StorageIcon fontSize="small" color="action" /></ListItemIcon>
                <ListItemText primary="Scope:" secondary="App Registration is global; Service Principal is local to a tenant." />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{minWidth: 30, mt: 0.5}}><FingerprintIcon fontSize="small" color="action" /></ListItemIcon>
                <ListItemText primary="Identity:" secondary="The App Registration has an Application (Client) ID. The Service Principal has its own Object ID and also references the Application ID of its blueprint." />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{minWidth: 30, mt: 0.5}}><SecurityUpdateGoodIcon fontSize="small" color="action" /></ListItemIcon>
                <ListItemText primary="Security Context:" secondary="Permissions are requested by the App Registration but granted to the Service Principal in each tenant. Conditional Access policies target Service Principals." />
              </ListItem>
               <ListItem>
                <ListItemIcon sx={{minWidth: 30, mt: 0.5}}><InfoIcon fontSize="small" color="action" /></ListItemIcon>
                <ListItemText primary="Multi-tenancy:" secondary="A multi-tenant app registration will have one service principal in its home tenant and one service principal in each consumer tenant where it's provisioned." />
              </ListItem>
            </List>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Understanding this distinction is crucial for managing application access and security in Microsoft Entra ID.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default ServicePrincipalOverview;